import { PayloadAction } from '@reduxjs/toolkit';
import { WritableDraft } from 'immer/dist/internal';

import { actions } from '.';
import { RootState, store } from '../../../App/store';
import shipmentStatuses from '../../../shared/shipment-statuses.json';
import { getAsync } from '../../../utils/api';
import { CustomProductInfo, RegisteredProductInfo } from '../New/Components/ProductList/SelectorInput';

export type Product = {
  uuid: string;
  productInfo?: RegisteredProductInfo | CustomProductInfo;
  description?: string;
  KDV?: number;
  count?: number;
  sellPrice?: number;
  isShipped?: boolean;
};

export type Discount = {
  percentage?: number;
  currency?: number;
};

export type Payment = {
  uuid: string;
  paidAt?: number;
  type?: string;
  amount?: number;
  bankId?: string;
  installments?: string;
  isPaid?: boolean;
  isEditingLocked?: boolean;
};

export type ShipmentStatus = typeof shipmentStatuses[number]["key"];
export type ShipmentInfo = {
  status: ShipmentStatus;
  // specific date
  dateTimestamp?: number;
  // date range
  dateStartTimestamp?: number;
  dateEndTimestamp?: number;
  version?: 'v2';
};

export type Employee = {
  id: string;
  pin: string;
  name: string;
};

export type Account = {
  id: string;
  versionId: string;
  displayName: string;
};

export type FormVariables = {
  UUID?: string;
  products?: { [uuid: string]: Product };
  discount?: Discount;
  payments?: { [uuid: string]: Payment };
  activeAccount?: Account;
  isAccountModalOpen?: boolean;
  editingFormId?: string;
  employee?: Employee;
  shipmentInfo?: ShipmentInfo;
  created?: number;
  note?: string;
  isSpecialOrder?: boolean;
  editorWindowId?: string;
  isCreatedOnMobile?: boolean;
  serial?: string;
  _productsDeleteSelection?: string[];
  _paymentsDeleteSelection?: string[];
};

type WD = WritableDraft<FormVariables & { [key: string]: any }>;

const newFormReducers = {
  reset: (state: WD) => {
    for (const key in state)
      if (Object.prototype.hasOwnProperty.call(state, key)) delete state[key];
  },

  setFormVariable: <T extends keyof FormVariables>(
    state: WD,
    action: PayloadAction<[T, FormVariables[T]]>
  ) => {
    const [key, value] = action.payload;
    state[key] = value;
  },

  setFormData: (state: WD, action: PayloadAction<any>) => {
    const data = action.payload;
    const makeObj = (arr: any) =>
      Object.fromEntries(arr.map((v: any) => [v.uuid, v]));

    state.created = data.created;
    state.activeAccount = data.account;
    state.shipmentInfo = data.shipment;
    state.discount = data.discount;
    state.note = data.note;
    state.payments = makeObj(data.payments);
    state.products = makeObj(data.products);
  },

  setFormProductVariable: <T extends keyof Product>(
    state: WD,
    action: PayloadAction<[string, T, Product[T]]>
  ) => {
    const [uuid, key, value] = action.payload;
    let stateProducts = state.products;

    if (!stateProducts) {
      stateProducts = {};
      state.products = stateProducts;
    }

    let product: Product | undefined = stateProducts[uuid];

    if (!product) {
      product = { uuid };
      stateProducts[uuid] = product;
    }

    product[key] = value;
  },

  removeFormProduct: (state: WD, action: PayloadAction<string>) => {
    const uuid = action.payload;
    const stateProducts = state.products;

    if (stateProducts && stateProducts[uuid]) {
      delete stateProducts[uuid];
    }
  },

  setFormDiscountValue: (
    state: WD,
    action: PayloadAction<[keyof Discount, number | undefined]>
  ) => {
    const [key, value] = action.payload;
    let { discount } = state;

    if (!discount) {
      discount = {};
      state.discount = discount;
    }

    discount[key] = value;
  },

  setFormPaymentVariable: <T extends keyof Payment>(
    state: WD,
    action: PayloadAction<[string, T, Payment[T]]>
  ) => {
    const [uuid, key, value] = action.payload;
    let statePayments = state.payments;

    if (!statePayments) {
      statePayments = {};
      state.payments = statePayments;
    }

    let payment: Payment | undefined = statePayments[uuid];

    if (!payment) {
      payment = { uuid };
      statePayments[uuid] = payment;
    }

    payment[key] = value;
  },

  removeFormPayment: (state: WD, action: PayloadAction<string>) => {
    const uuid = action.payload;
    const statePayments = state.payments;

    if (statePayments && statePayments[uuid]) {
      delete statePayments[uuid];
    }
  },

  setShipmentType: (state: WD, action: PayloadAction<ShipmentStatus>) => {
    if (!state.shipmentInfo) state.shipmentInfo = { status: action.payload };
    else state.shipmentInfo.status = action.payload;
  },

  setShipmentDateTimestamp: (state: WD, action: PayloadAction<number>) => {
    if (!state.shipmentInfo) throw new Error("no shipment info");

    state.shipmentInfo.dateTimestamp = action.payload;
  },
  setShipmentStartDateTimestamp: (state: WD, action: PayloadAction<number>) => {
    if (!state.shipmentInfo) throw new Error("no shipment info");

    state.shipmentInfo.dateStartTimestamp = action.payload;
  },
  setShipmentEndDateTimestamp: (state: WD, action: PayloadAction<number>) => {
    if (!state.shipmentInfo) throw new Error("no shipment info");

    state.shipmentInfo.dateEndTimestamp = action.payload;
  },
};

for (const key in newFormReducers)
  if (Object.prototype.hasOwnProperty.call(newFormReducers, key)) {
    const key2 = key as keyof typeof newFormReducers;
    const fn = newFormReducers[key2];

    newFormReducers[key2] = ((
      state: WritableDraft<RootState["currentForm"]>,
      action: PayloadAction<any>
    ) => (fn as any)(state.newForms[state.editingNewFormUUID], action)) as any;
  }

export default newFormReducers;
export const getFormState = () => {
  const state = store.getState();
  return state.currentForm.newForms[state.currentForm.editingNewFormUUID];
};
export const setFormsActiveAccount = async (id: string, versionId?: string) => {
  const res = await getAsync(
    `/accounts/get-info?id=${id}&versionId=${versionId || ""}`
  );

  if (res.success) {
    actions.setFormVariable(["activeAccount", res.data]);
    actions.setFormVariable(["isAccountModalOpen", false]);
  }
};
